import React, {useEffect, useRef} from 'react';
import {Animated, Easing, Pressable, StyleSheet, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import {PlayerProfileFull} from '@/Hooks/PlayerProfileApi/types';
import {useGetPlayerGamesApi} from '@/Hooks/PlayerProfileApi/useGetPlayerGamesApi';
import ThunderText from '../../ui/ThunderText';

// Recent games (this Overview endpoint) carries no `uuid` -- the Games tab's
// endpoint already does (get_player_games), and both list the same games
// order (-end_time). Rather than add a field to the Overview backend, reuse
// the Games tab's own data/hook (same react-query cache key if that tab's
// already been visited -- no extra request) and match rows by end_time +
// opponent to find each one's uuid.
const gameRowKey = (endTime: unknown, opponent: unknown) =>
  `${endTime ?? ''}|${opponent ?? ''}`;

// Scorecard bar that grows up from the baseline on mount (spec §5 "Bar
// grow-in": 0.55s ease-out, staggered). Animating the height % gives the
// same bottom-anchored reveal as the design's scaleY(0->1).
function GrowBar({
  heightPct,
  color,
  delay,
}: {
  heightPct: number;
  color: string;
  delay: number;
}) {
  const v = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const anim = Animated.timing(v, {
      toValue: 1,
      duration: 550,
      delay,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    });
    anim.start();
    return () => anim.stop();
  }, [v, delay, heightPct]);
  const height = v.interpolate({
    inputRange: [0, 1],
    outputRange: ['0%', `${heightPct}%`],
  });
  return (
    <Animated.View
      style={{
        width: '100%',
        borderTopLeftRadius: 5,
        borderTopRightRadius: 5,
        height,
        backgroundColor: color,
      }}
    />
  );
}

const styles = StyleSheet.create({
  columns: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  leftCol: {
    flex: 2,
    minWidth: 280,
  },
  rightCol: {
    flex: 1,
    minWidth: 240,
  },
  // Spec §2 "Overview cards": radius 20, padding 24/26, soft card shadow,
  // no border (the shadow does the separating, not a hairline outline).
  card: {
    backgroundColor: '#fff',
    borderRadius: 20,
    paddingVertical: 24,
    paddingHorizontal: 26,
    marginBottom: 18,
    shadowColor: 'rgba(77,63,55,1)',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  cardHeaderRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  // Design handoff's section headers are 18px semibold dark titles (not the
  // tiny uppercase-grey micro-labels we had), with an optional grey subtitle
  // underneath.
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#262322',
  },
  sectionSubtitle: {
    fontSize: 12,
    color: '#888280',
    marginTop: 3,
  },
  // Plain teal text in the design, not a green pill badge.
  winRateText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#3DAB9E',
  },
  // Stacked won/drawn/lost proportion bar -- a prominent design element that
  // was missing entirely before.
  resultsBar: {
    flexDirection: 'row',
    height: 14,
    borderRadius: 999,
    overflow: 'hidden',
    marginBottom: 16,
  },
  resultsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 26,
    marginBottom: 16,
  },
  resultStat: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  resultDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  resultValue: {
    fontSize: 15,
    fontWeight: '600',
    color: '#262322',
  },
  resultLabel: {
    fontSize: 11,
    color: '#888280',
  },
  // Averages sit on the same row as Won/Drawn/Lost, pushed to the right edge
  // (design: `margin-left:auto`); wraps beneath on a narrow card.
  avgGroup: {
    marginLeft: 'auto',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 16,
  },
  // Stacked like the Won/Drawn/Lost items: value on top, label beneath.
  avgItem: {
    alignItems: 'flex-start',
  },
  avgValue: {
    fontSize: 15,
    fontWeight: '600',
    color: '#262322',
  },
  avgLabel: {
    fontSize: 11,
    color: '#888280',
  },
  // Owner-only cards (Daily scorecard / Opening breakdown) below Results --
  // a visitor viewing someone else's profile doesn't see these.
  scorecardChart: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 6,
    height: 90,
    marginBottom: 8,
  },
  scorecardBarCol: {
    flex: 1,
    alignItems: 'center',
  },
  // Count sits directly on top of each bar (inside the flex-end track, above
  // the growing bar), so it rides the bar's height. Light weight + muted grey
  // so it reads as an annotation, not a heading.
  scorecardCount: {
    fontSize: 10,
    fontWeight: '400',
    color: '#A8A7A6',
    textAlign: 'center',
    marginBottom: 2,
  },
  scorecardBarTrack: {
    width: '100%',
    height: 70,
    justifyContent: 'flex-end',
  },
  scorecardBar: {
    width: '100%',
    borderRadius: 4,
    minHeight: 3,
  },
  scorecardDateLabel: {
    fontSize: 9,
    color: '#A8A7A6',
    marginTop: 4,
  },
  scorecardEmpty: {
    fontSize: 12,
    color: '#888280',
  },
  scoreStatsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(38,35,34,0.08)',
  },
  scoreStat: {
    alignItems: 'flex-start',
  },
  scoreStatLabel: {
    fontSize: 11,
    color: '#888280',
    marginTop: 4,
  },
  openingRow: {
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EDE7',
  },
  openingRowTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  openingName: {
    fontSize: 13,
    color: '#262322',
    flex: 1,
  },
  openingMeta: {
    fontSize: 12,
    color: '#888280',
  },
  // Spec §2: each opening row has a 6px thin progress bar beneath it.
  openingBarTrack: {
    height: 6,
    borderRadius: 999,
    backgroundColor: '#F5EDD3',
    overflow: 'hidden',
    marginTop: 7,
  },
  openingBarFill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: '#3DAB9E',
  },
  gameRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EDE7',
  },
  // Result-colored rounded square (W/D/L), per the design -- not a teal
  // circle avatar.
  gameResultBadge: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  gameResultBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#fff',
  },
  gameOpponentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  gameOpponentNameRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 6,
  },
  gameOpponent: {
    fontSize: 13,
    fontWeight: '600',
    color: '#262322',
    flexShrink: 1,
  },
  gameOpponentLinked: {
    color: '#3DAB9E',
    textDecorationLine: 'underline',
  },
  gameOpponentRating: {
    fontSize: 11,
    color: '#888280',
  },
  gameFormat: {
    fontSize: 11,
    color: '#888280',
    marginTop: 2,
  },
  gameRightCol: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  gameStar: {
    fontSize: 14,
    color: '#E7B32B',
  },
  gameResult: {
    fontSize: 13,
    fontWeight: '600',
  },
  emptyText: {
    fontSize: 12,
    color: '#888280',
  },
  seeAllLink: {
    fontSize: 13,
    fontWeight: '600',
    color: '#3DAB9E',
  },
});

// Opening bars use a rotating categorical palette by row index (design's
// "Openings played" card: teal / gold / blue / orange), not a single flat
// teal for every row.
const OPENING_BAR_COLORS = ['#3DAB9E', '#FACF47', '#67B3E1', '#E17846'];

// Rating delta text color follows the sign of the change (delta tokens).
const deltaColor = (delta: number | null) => {
  if (delta == null || delta === 0) {
    return '#888280';
  }
  return delta > 0 ? '#2F7F4C' : '#CD2C39';
};

const endReasonLabel = (reason: string | null) => {
  if (!reason || reason === 'other') {
    return null;
  }
  return reason.charAt(0).toUpperCase() + reason.slice(1);
};

// Time controls are stored base-in-seconds ("Blitz 180+2"); the design shows
// minutes ("Blitz 3+2").
const displayFormat = (format: string | null) => {
  if (!format) {
    return '';
  }
  return format.replace(/(\d+)\s*\+\s*(\d+)/, (whole, base, inc) => {
    const secs = Number(base);
    if (secs < 60) {
      return whole;
    }
    const mins = secs / 60;
    const minStr = Number.isInteger(mins) ? String(mins) : mins.toFixed(1);
    return `${minStr}+${inc}`;
  });
};

// Open another player's profile. `push` (not `navigate`) so a chain of
// nested profiles becomes real stack entries -- the back button can then step
// back one profile at a time instead of jumping straight to the original
// entry point. Falls back to navigate for navigators without push.
const openProfile = (navigation: any, username?: string | null) => {
  if (!username) {
    return;
  }
  if (navigation?.push) {
    navigation.push('PlayerProfile', {username});
  } else {
    navigation?.navigate?.('PlayerProfile', {username});
  }
};

const timeAgo = (iso: string | null) => {
  if (!iso) {
    return '';
  }
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return '';
  }
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) {
    return 'Today';
  }
  if (days === 1) {
    return 'Yesterday';
  }
  return `${days} days ago`;
};

type Props = {
  profile: PlayerProfileFull;
  navigation?: any;
  onSeeAllGames?: () => void;
  isOwner?: boolean;
};

export default function OverviewTab({
  profile,
  navigation,
  onSeeAllGames,
  isOwner = false,
}: Props) {
  const {
    results_breakdown,
    daily_scorecard,
    opening_breakdown,
    recent_games,
  } = profile;

  // Owner-only -- the review action this feeds is itself owner-only (see
  // the row press-handler below).
  const gamesQ = useGetPlayerGamesApi(
    isOwner ? profile.username ?? undefined : undefined,
    1,
    {},
  );
  const recentGameUuidByKey: Record<string, string> = {};
  gamesQ.data?.data?.forEach(g => {
    if (g.uuid) {
      recentGameUuidByKey[gameRowKey(g.end_time, g.opponent)] = g.uuid;
    }
  });

  const totalGames =
    (results_breakdown?.won ?? 0) +
    (results_breakdown?.drawn ?? 0) +
    (results_breakdown?.lost ?? 0);
  const winRatePct =
    totalGames > 0
      ? Math.round(((results_breakdown?.won ?? 0) / totalGames) * 100)
      : null;

  // Daily scorecard / Opening breakdown are owner-only (a visitor viewing
  // someone else's profile doesn't see these) -- only compute their derived
  // data when it'll actually render.
  const maxGamesPlayed = Math.max(
    1,
    ...(daily_scorecard ?? []).map(r => r.games_played ?? 0),
  );
  const scorecard = daily_scorecard ?? [];
  const scGamesPlayed = scorecard.reduce(
    (sum, r) => sum + (r.games_played ?? 0),
    0,
  );
  const scActiveDays = scorecard.filter(r => (r.games_played ?? 0) > 0).length;
  const accVals = scorecard
    .map(r => r.average_accuracy)
    .filter((a): a is number => a != null);
  const scAvgAccuracy = accVals.length
    ? Math.round(accVals.reduce((sum, a) => sum + a, 0) / accVals.length)
    : null;
  const netVals = scorecard
    .map(r => r.net_score)
    .filter((n): n is number => n != null);
  const scNetScore = netVals.length
    ? netVals.reduce((sum, n) => sum + n, 0)
    : null;

  // Drop rows with no real opening name -- the backend can send "Unknown"
  // (or an empty string) for games it couldn't classify; those shouldn't
  // appear as an opening in the breakdown.
  const openings = (opening_breakdown ?? []).filter(
    r => r.opening && r.opening.trim().toLowerCase() !== 'unknown',
  );
  const maxOpeningCount = Math.max(1, ...openings.map(r => r.count ?? 0));

  return (
    <View style={styles.columns}>
      <View style={styles.leftCol}>
        <Box style={styles.card}>
          <View style={styles.cardHeaderRow}>
            <View>
              <CCText style={styles.sectionTitle}>Results</CCText>
              <CCText style={styles.sectionSubtitle}>
                {totalGames} rated games
              </CCText>
            </View>
            {winRatePct != null ? (
              <CCText style={styles.winRateText}>{winRatePct}% win rate</CCText>
            ) : null}
          </View>

          {totalGames > 0 ? (
            <View style={styles.resultsBar}>
              <View
                style={{
                  flex: results_breakdown?.won ?? 0,
                  backgroundColor: '#3DAB9E',
                }}
              />
              <View
                style={{
                  flex: results_breakdown?.drawn ?? 0,
                  backgroundColor: '#FACF47',
                }}
              />
              <View
                style={{
                  flex: results_breakdown?.lost ?? 0,
                  backgroundColor: '#E17846',
                }}
              />
            </View>
          ) : null}

          <View style={styles.resultsRow}>
            <View style={styles.resultStat}>
              <View style={[styles.resultDot, {backgroundColor: '#3DAB9E'}]} />
              <View>
                <CCText style={styles.resultValue}>
                  {results_breakdown?.won ?? 0}
                </CCText>
                <CCText style={styles.resultLabel}>Won</CCText>
              </View>
            </View>
            <View style={styles.resultStat}>
              <View style={[styles.resultDot, {backgroundColor: '#FACF47'}]} />
              <View>
                <CCText style={styles.resultValue}>
                  {results_breakdown?.drawn ?? 0}
                </CCText>
                <CCText style={styles.resultLabel}>Drawn</CCText>
              </View>
            </View>
            <View style={styles.resultStat}>
              <View style={[styles.resultDot, {backgroundColor: '#E17846'}]} />
              <View>
                <CCText style={styles.resultValue}>
                  {results_breakdown?.lost ?? 0}
                </CCText>
                <CCText style={styles.resultLabel}>Lost</CCText>
              </View>
            </View>

            <View style={styles.avgGroup}>
              {results_breakdown?.avg_opponent_rating != null && (
                <View style={styles.avgItem}>
                  <CCText style={styles.avgValue}>
                    {Math.round(results_breakdown.avg_opponent_rating)}
                  </CCText>
                  <CCText style={styles.avgLabel}>Avg opponent</CCText>
                </View>
              )}
              {results_breakdown?.best_win != null && (
                <View style={styles.avgItem}>
                  <CCText style={styles.avgValue}>
                    {results_breakdown.best_win}
                  </CCText>
                  <CCText style={styles.avgLabel}>Best win</CCText>
                </View>
              )}
              {results_breakdown?.avg_accuracy != null && (
                <View style={styles.avgItem}>
                  <CCText style={styles.avgValue}>
                    {Math.round(results_breakdown.avg_accuracy)}%
                  </CCText>
                  <CCText style={styles.avgLabel}>Avg accuracy</CCText>
                </View>
              )}
            </View>
          </View>
        </Box>

        {isOwner ? (
          <>
            <Box style={styles.card}>
              <CCText style={styles.sectionTitle}>
                Daily scorecard · last 14 days
              </CCText>
              <View style={{marginTop: 12}}>
                {daily_scorecard?.length ? (
                  <View style={styles.scorecardChart}>
                    {daily_scorecard.map((row, idx) => {
                      // Design colors by games played that day, not accuracy:
                      // none = faint, a heavy day (>=9) = teal, otherwise gold.
                      const games = row.games_played ?? 0;
                      const barColor =
                        games === 0
                          ? '#EFE3C4'
                          : games >= 9
                          ? '#3DAB9E'
                          : '#FACF47';
                      const heightPct = Math.max(
                        4,
                        ((row.games_played ?? 0) / maxGamesPlayed) * 100,
                      );
                      return (
                        <View
                          key={`${row.date}-${idx}`}
                          style={styles.scorecardBarCol}>
                          <View style={styles.scorecardBarTrack}>
                            {games > 0 ? (
                              <CCText style={styles.scorecardCount}>
                                {games}
                              </CCText>
                            ) : null}
                            <GrowBar
                              heightPct={heightPct}
                              color={barColor}
                              delay={idx * 30}
                            />
                          </View>
                          <CCText
                            style={styles.scorecardDateLabel}
                            numberOfLines={1}>
                            {/* Day-of-month only ("1", "14") -- with 14
                                columns, "MM-DD" no longer fits a column's
                                width on a narrow viewport and was getting
                                ellipsis-truncated to "0…"/"07…". */}
                            {row.date ? String(Number(row.date.slice(8, 10))) : ''}
                          </CCText>
                        </View>
                      );
                    })}
                  </View>
                ) : (
                  <CCText style={styles.scorecardEmpty}>
                    No recent activity.
                  </CCText>
                )}
              </View>

              {daily_scorecard?.length ? (
                <View style={styles.scoreStatsRow}>
                  <View style={styles.scoreStat}>
                    <ThunderText size={30} lineHeight={30}>
                      {scGamesPlayed}
                    </ThunderText>
                    <CCText style={styles.scoreStatLabel}>Games played</CCText>
                  </View>
                  <View style={styles.scoreStat}>
                    <ThunderText size={30} lineHeight={30} color="#3DAB9E">
                      {scActiveDays}
                    </ThunderText>
                    <CCText style={styles.scoreStatLabel}>Active days</CCText>
                  </View>
                  {scAvgAccuracy != null ? (
                    <View style={styles.scoreStat}>
                      <ThunderText size={30} lineHeight={30}>
                        {scAvgAccuracy}%
                      </ThunderText>
                      <CCText style={styles.scoreStatLabel}>
                        Avg accuracy
                      </CCText>
                    </View>
                  ) : null}
                  {scNetScore != null ? (
                    <View style={styles.scoreStat}>
                      <ThunderText
                        size={30}
                        lineHeight={30}
                        color={scNetScore >= 0 ? '#3DAB9E' : '#E17846'}>
                        {scNetScore > 0 ? `+${scNetScore}` : scNetScore}
                      </ThunderText>
                      <CCText style={styles.scoreStatLabel}>Net score</CCText>
                    </View>
                  ) : null}
                </View>
              ) : null}
            </Box>

            <Box style={styles.card}>
              <CCText style={styles.sectionTitle}>Opening breakdown</CCText>
              {openings.length ? (
                openings.map((row, idx) => (
                  <View key={`${row.opening}-${idx}`} style={styles.openingRow}>
                    <View style={styles.openingRowTop}>
                      <CCText style={styles.openingName} numberOfLines={1}>
                        {row.opening}
                      </CCText>
                      <CCText style={styles.openingMeta}>
                        {row.color} · {row.count}
                      </CCText>
                    </View>
                    <View style={styles.openingBarTrack}>
                      <View
                        style={[
                          styles.openingBarFill,
                          {
                            width: `${((row.count ?? 0) / maxOpeningCount) * 100}%`,
                            backgroundColor:
                              OPENING_BAR_COLORS[idx % OPENING_BAR_COLORS.length],
                          },
                        ]}
                      />
                    </View>
                  </View>
                ))
              ) : (
                <CCText style={styles.emptyText}>
                  No openings recorded yet.
                </CCText>
              )}
            </Box>
          </>
        ) : null}
      </View>

      <View style={styles.rightCol}>
        <Box style={styles.card}>
          <View style={styles.cardHeaderRow}>
            <CCText style={styles.sectionTitle}>Recent games</CCText>
            {recent_games?.length && onSeeAllGames ? (
              <Pressable testID="recent-games-see-all" onPress={onSeeAllGames}>
                <CCText style={styles.seeAllLink}>See all</CCText>
              </Pressable>
            ) : null}
          </View>
          {recent_games?.length ? (
            recent_games.map((row, idx) => {
              const rowUuid =
                recentGameUuidByKey[gameRowKey(row.end_time, row.opponent)];
              const badgeColor =
                row.result === 'Win'
                  ? '#3DAB9E'
                  : row.result === 'Loss'
                  ? '#E17846'
                  : '#FACF47';
              const badgeLetter =
                row.result === 'Win'
                  ? 'W'
                  : row.result === 'Loss'
                  ? 'L'
                  : 'D';
              const metaLine = [
                displayFormat(row.format),
                timeAgo(row.end_time),
                endReasonLabel(row.end_reason),
              ]
                .filter(Boolean)
                .join(' · ');
              const content = (
                <>
                  <View style={styles.gameOpponentRow}>
                    <View
                      style={[
                        styles.gameResultBadge,
                        {backgroundColor: badgeColor},
                      ]}>
                      <CCText style={styles.gameResultBadgeText}>
                        {badgeLetter}
                      </CCText>
                    </View>
                    <View style={{flex: 1, minWidth: 0}}>
                      <View style={styles.gameOpponentNameRow}>
                        {/* Backend-resolved: only Circlechess-platform games
                            with a real CCUser opponent carry
                            opponent_cc_username; null otherwise. */}
                        {row.opponent_cc_username ? (
                          <Pressable
                            testID="recent-game-opponent-link"
                            // Independent of the row's own press (opens the
                            // game review) -- tapping the username specifically
                            // opens the opponent's profile instead.
                            onPress={e => {
                              e?.stopPropagation?.();
                              openProfile(navigation, row.opponent_cc_username);
                            }}>
                            <CCText
                              style={[styles.gameOpponent, styles.gameOpponentLinked]}
                              numberOfLines={1}>
                              {row.opponent ?? 'Unknown'}
                            </CCText>
                          </Pressable>
                        ) : (
                          <CCText style={styles.gameOpponent} numberOfLines={1}>
                            {row.opponent ?? 'Unknown'}
                          </CCText>
                        )}
                        {row.opponent_rating != null ? (
                          <CCText style={styles.gameOpponentRating}>
                            {row.opponent_rating}
                          </CCText>
                        ) : null}
                      </View>
                      {metaLine ? (
                        <CCText style={styles.gameFormat} numberOfLines={1}>
                          {metaLine}
                        </CCText>
                      ) : null}
                    </View>
                  </View>
                  <View style={styles.gameRightCol}>
                    <CCText
                      style={[
                        styles.gameResult,
                        {color: deltaColor(row.rating_delta)},
                      ]}>
                      {row.rating_delta != null
                        ? `${row.rating_delta > 0 ? '+' : ''}${row.rating_delta}`
                        : row.result}
                    </CCText>
                    {row.is_favourite ? (
                      <CCText style={styles.gameStar}>★</CCText>
                    ) : null}
                  </View>
                </>
              );

              // The row opens this game's review -- owner-only (a visitor
              // can see that a game exists but not open its full analysis;
              // the backend enforces this too, see game/views.py's get_games
              // ownership check). The opponent name (above) independently
              // opens their profile via its own Pressable, regardless of
              // owner-ness. Plain browser navigation (not navigation.navigate)
              // -- same window.location.origin-based URL building the share
              // button already uses.
              if (rowUuid && isOwner) {
                return (
                  <Pressable
                    key={rowUuid}
                    testID="recent-game-row"
                    style={styles.gameRow}
                    onPress={() => {
                      window.location.href = `${window.location.origin}/chessAnalysis?game_id=${rowUuid}`;
                    }}>
                    {content}
                  </Pressable>
                );
              }

              return (
                <View
                  key={`${row.opponent}-${idx}`}
                  testID="recent-game-row"
                  style={styles.gameRow}>
                  {content}
                </View>
              );
            })
          ) : (
            <CCText style={styles.emptyText}>No games played yet.</CCText>
          )}
        </Box>
      </View>
    </View>
  );
}
